import { IconRegularCalendar, IconRegularClock, IconRegularPhone, Ratings } from '@notino/react-styleguide'
import React from 'react'
import { useIntl } from 'react-intl'

// components
import SalonDetailPlaceholder from '../SalonDetailPlaceholder/SalonDetailPlaceholder'
import SalonImage from '../SalonImage/SalonImage'
import VisuallyHidden from '../../atoms/VisuallyHidden/VisuallyHidden'

// types
import { Salon } from '../../types/types'

// utils
import { AVAILABLE_RESERVATION_TYPE } from '../../utils/enums'
import { formatDistance, formatRating } from '../../utils/helper'

// hooks
import useMessages from '../../hooks/useMessages'

// styles
import * as SC from './SalonDetailStyles'
import { SALON_DETAIL } from '../../styles/constants'

// assets
import IconSolidPin from '../../assets/icons/IconSolidPin'

// types
type SalonDetailProps = {
	salon: Salon
	isLoading?: boolean
	imageLoading?: React.ImgHTMLAttributes<HTMLImageElement>['loading']
	href: string
	onClick?: React.MouseEventHandler<HTMLAnchorElement>
}

// component
const SalonDetail: React.FC<React.PropsWithChildren<SalonDetailProps>> = (props) => {
	const { salon, isLoading = false, imageLoading, href, onClick } = props
	const { messages } = useMessages()
	const intl = useIntl()

	if (isLoading) {
		return <SalonDetailPlaceholder />
	}

	const ratingAccessibleText =
		salon.rating !== undefined
			? intl.formatMessage(
					{
						id: 'Average rating: {rating} out of 5, {reviewCount, plural, one {# review} other {# reviews}}',
						defaultMessage: 'Average rating: {rating} out of 5, {reviewCount, plural, one {# review} other {# reviews}}'
					},
					{
						rating: formatRating(intl, salon.rating),
						reviewCount: salon.ratesCount
					}
				)
			: undefined

	const formattedDistance = salon.distance !== undefined ? formatDistance(salon.distance) : ''
	const distanceAccessibleText = formattedDistance
		? intl.formatMessage(
				{
					id: 'Distance: {distance}',
					defaultMessage: 'Distance: {distance}'
				},
				{ distance: formattedDistance }
			)
		: undefined

	return (
		<SC.SalonDetailItem href={href} onClick={onClick}>
			<SC.ImageWrapper>
				{salon.availableReservationType === AVAILABLE_RESERVATION_TYPE.ONLINE_RESERVATION && (
					<SC.AvailableRSBadge>
						<IconRegularCalendar color={'icon.primary'} width={'1rem'} height={'1rem'} />
						{messages['Online reservation']}
					</SC.AvailableRSBadge>
				)}
				{salon.availableReservationType === AVAILABLE_RESERVATION_TYPE.PHONE_RESERVATION && (
					<SC.AvailableRSBadge>
						<IconRegularPhone color={'icon.primary'} width={'1rem'} height={'1rem'} />
						{messages['Phone reservation only']}
					</SC.AvailableRSBadge>
				)}
				<SalonImage salon={salon} loading={imageLoading} width={SALON_DETAIL.IMAGE_WIDTH} height={SALON_DETAIL.IMAGE_HEIGHT} />
			</SC.ImageWrapper>

			<SC.TextContentWrapper>
				{(salon.rating || salon.distance !== undefined) && (
					<SC.TextContentHeader>
						{salon.rating && ratingAccessibleText && (
							<SC.RatingWrapper>
								<VisuallyHidden>{ratingAccessibleText}</VisuallyHidden>
								<Ratings
									size={14}
									foreground={'text.primary'}
									background={'icon.disabled'}
									rating={salon.rating}
									ratingId={`${salon.id}-${salon.rating}`}
									aria-hidden={'true'}
								/>
								<SC.Rating aria-hidden={'true'}>{formatRating(intl, salon.rating)}</SC.Rating>
								<SC.RatingCounts aria-hidden={'true'}>({salon.ratesCount})</SC.RatingCounts>
							</SC.RatingWrapper>
						)}
						{salon.distance !== undefined && distanceAccessibleText && (
							<SC.DistanceWrapper>
								<VisuallyHidden>{distanceAccessibleText}</VisuallyHidden>
								<IconSolidPin aria-hidden={'true'} />
								<span aria-hidden={'true'}>{formattedDistance}</span>
							</SC.DistanceWrapper>
						)}
					</SC.TextContentHeader>
				)}

				{salon.name && <SC.Title>{salon.name}</SC.Title>}

				<SC.Description>
					{salon.formattedAddress && <SC.Address>{salon.formattedAddress}</SC.Address>}

					{salon.openingHoursData && salon.openingHoursData.isOpen && (
						<SC.OpeningHoursMessage dangerouslySetInnerHTML={{ __html: salon.openingHoursData.message }} />
					)}
					{salon.openingHoursData && !salon.openingHoursData.isOpen && (
						<SC.OpeningHours>
							<IconRegularClock color={'icon.primary'} width={'1rem'} height={'1rem'} />
							<SC.OpeningHoursMessage dangerouslySetInnerHTML={{ __html: salon.openingHoursData.message }} />
						</SC.OpeningHours>
					)}
				</SC.Description>
			</SC.TextContentWrapper>
		</SC.SalonDetailItem>
	)
}

export default SalonDetail
